import React, { useState } from 'react';
import propTypes from 'prop-types';
import { ChevronRight, Plus, StickyNote } from 'lucide-react';
import Card from 'tailwind-components/Card/Card';
import dateFactory from 'helpers/factory/dateFactory';
import { stringTrimmer } from 'helpers/strings';
import CreateNoteForm from './CreateNoteForm/CreateNoteForm';

const CLOSED_MODAL = {
  active: false,
  title: '',
  description: '',
  id: '',
  date: ''
};

const noteRowClassName =
  'group flex w-full cursor-pointer items-center gap-2.5 rounded-xl border border-grey-lighter/70 bg-white ' +
  'px-3 py-2 text-left transition-all hover:border-primary-emphasis-light hover:bg-primary-lighter';

function NotesSkeleton() {
  return (
    <div className='flex flex-col gap-2'>
      {Array.from({ length: 3 }, (_, i) => (
        <div
          key={i}
          className='h-12 animate-pulse rounded-xl bg-grey-lightest'
        />
      ))}
    </div>
  );
}

function NotesEmptyState() {
  return (
    <div className='flex flex-1 flex-col items-center justify-center gap-1.5 rounded-xl border border-dashed border-grey-lighter py-6 text-grey-light'>
      <StickyNote className='size-6' aria-hidden />
      <p className='m-0 text-xs'>No notes yet</p>
    </div>
  );
}

/**
 * the agent's portal notes, with the create/edit modal attached
 * @param notes - notes to list, newest first
 * @param loading - whether the dashboard data is still being fetched
 */
export default function NotesCard({ notes = [], loading = false }) {
  const [modalData, setModalData] = useState(CLOSED_MODAL);

  function openNote(note) {
    setModalData({ ...CLOSED_MODAL, ...note, active: true });
  }

  function closeModal() {
    setModalData(prev => ({ ...prev, active: false }));
  }

  function renderBody() {
    if (loading) return <NotesSkeleton />;
    if (notes.length === 0) return <NotesEmptyState />;
    return (
      <div className='show-scrollbar flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto pr-1'>
        {notes.map(note => (
          <button
            key={note.id}
            type='button'
            onClick={() => openNote(note)}
            className={noteRowClassName}
          >
            <span className='min-w-0 flex-1'>
              <span className='block truncate text-[13px] font-semibold text-primary'>
                {stringTrimmer(note.title ?? '', 80)}
              </span>
              {note.date && (
                <span className='mt-0.5 block text-[11px] text-grey-light'>
                  {dateFactory(note.date)}
                </span>
              )}
            </span>
            <ChevronRight className='size-4 shrink-0 text-grey-lighter transition-transform group-hover:translate-x-0.5 group-hover:text-primary-emphasis-light' />
          </button>
        ))}
      </div>
    );
  }

  return (
    <Card
      variant='elevated'
      title='Notes'
      icon={<StickyNote aria-hidden />}
      className='min-h-[180px] lg:min-h-0 lg:flex-1'
      action={
        notes.length > 0 && (
          <span className='shrink-0 rounded-full bg-grey-lightest px-2 py-0.5 text-[11px] font-bold tabular-nums text-grey'>
            {notes.length}
          </span>
        )
      }
    >
      {renderBody()}
      <button
        type='button'
        onClick={() => openNote({})}
        className='flex w-full shrink-0 cursor-pointer items-center justify-center gap-1.5 rounded-xl border border-dashed border-grey-lighter bg-transparent py-2 text-xs font-semibold text-primary-emphasis-light transition-colors hover:border-primary-emphasis-light hover:bg-primary-lighter'
      >
        <Plus className='size-4' aria-hidden />
        Add note
      </button>
      <CreateNoteForm closeModal={closeModal} {...modalData} />
    </Card>
  );
}

NotesCard.propTypes = {
  notes: propTypes.array,
  loading: propTypes.bool
};
